import { PresentationManifest } from '@sketch-hq/sketch-web-renderer'
import { useEffect, useState } from 'react'

type LoadManifestResult =
  | {
      status: 'idle'
      manifest: null
    }
  | {
      status: 'loading'
      manifest: null
      manifestUrl: string
    }
  | {
      status: 'error'
      manifest: null
      manifestUrl: string
    }
  | {
      status: 'success'
      manifest: PresentationManifest
      manifestUrl: string
    }

type UsePrototypeManifestFileProps = {
  manifestUrl?: string | null
}

/**
 * Simple JSON fetch to access the presentation manifest file.
 * @TODO Look into simplifying this and reducing large try/catch blocks.
 */
export function usePresentationManifest(
  props: UsePrototypeManifestFileProps
): LoadManifestResult {
  const { manifestUrl } = props

  const [result, setResult] = useState<LoadManifestResult>({
    status: 'idle',
    manifest: null,
  })

  useEffect(() => {
    async function loadManifest() {
      if (!manifestUrl) return // Skip while the manifestUrl is undefined

      const needsToFetch =
        result.status === 'idle' || manifestUrl !== result.manifestUrl

      if (!needsToFetch) {
        // Skip since we have already fetched. Only refetch if download URL has changed.
        return
      }

      try {
        setResult({
          status: 'loading',
          manifest: null,
          manifestUrl,
        })

        const res = await fetch(manifestUrl)

        if (!res.ok) {
          setResult({
            status: 'error',
            manifest: null,
            manifestUrl,
          })
          return
        }

        try {
          const manifest: unknown = await res.json()

          if (!validateManifest(manifest)) {
            setResult({
              status: 'error',
              manifest: null,
              manifestUrl,
            })
            return
          }

          setResult({ status: 'success', manifest, manifestUrl })
        } catch (parseError) {
          setResult({
            status: 'error',
            manifest: null,
            manifestUrl,
          })
        }
      } catch (fetchError) {
        setResult({
          status: 'error',
          manifest: null,
          manifestUrl,
        })
      }
    }

    loadManifest()
  }, [manifestUrl, result])

  return result
}

/**
 * Do an extremely basic smoke test on the Manifest data format.
 */
function validateManifest(manifest: unknown): manifest is PresentationManifest {
  if (
    manifest !== null &&
    typeof manifest === 'object' &&
    'contents' in manifest
  ) {
    return true
  } else {
    return false
  }
}
